import { useMemo, useReducer } from 'react';

import { WorkflowRunStep } from 'dashboard/models/workflow-step';
import {
  outputStep,
  WorkflowStep,
  WorkflowStepInput,
  WorkflowStepOutput,
} from 'dashboard/models/workflow-version';
import { buildGraph, TokensToNode } from 'lib/workflows';

type StepSelectAction =
  | {
      type: 'select';
      payload: { token: string | null; moduleToken: string | null };
    }
  | {
      type: 'focus';
      payload: string;
    };

type StepSelectState = {
  selectedToken: string | null;
  moduleToken: string | null;
  focusedToken: string | null;
};

const stepSelectReducer = (
  state: StepSelectState,
  { type, payload }: StepSelectAction,
): StepSelectState => {
  switch (type) {
    case 'select':
      return {
        ...state,
        selectedToken: payload.token,
        focusedToken: payload.moduleToken ? null : payload.token,
        moduleToken: payload.moduleToken,
      };
    case 'focus':
      return { ...state, focusedToken: payload };
    default:
      return state;
  }
};

const useWorkflowStepSelectionState = ({
  inputStep,
  moduleBlockSteps,
  moduleRunStepsByConfigStepToken,
  tokensToRunSteps,
  tokensToSteps,
}: {
  inputStep: WorkflowStepInput;
  moduleBlockSteps: Record<string, WorkflowStep[]>;
  moduleRunStepsByConfigStepToken: Record<string, WorkflowRunStep[]>;
  tokensToRunSteps: Record<string, WorkflowRunStep>;
  tokensToSteps: TokensToNode;
}) => {
  const [currentState, updateStepSelectState] = useReducer(stepSelectReducer, {
    selectedToken: null,
    moduleToken: null,
    focusedToken: null,
  });

  const tokensToModuleBlockSteps = useMemo(
    () =>
      Object.entries(moduleBlockSteps).reduce<Record<string, TokensToNode>>(
        (acc, [t, steps]) => ({ ...acc, [t]: buildGraph(steps) }),
        {},
      ),
    [moduleBlockSteps],
  );

  const { activeStep, activeRunStep } = useMemo<{
    activeStep: WorkflowStep | WorkflowStepInput | WorkflowStepOutput | null;
    activeRunStep: WorkflowRunStep | null;
  }>(() => {
    if (!currentState.selectedToken) return { activeStep: null, activeRunStep: null };
    if (currentState.selectedToken === outputStep.token)
      return { activeStep: outputStep, activeRunStep: null };

    if (currentState.moduleToken && tokensToModuleBlockSteps[currentState.moduleToken]) {
      return {
        activeStep:
          tokensToModuleBlockSteps[currentState.moduleToken]?.[currentState.selectedToken]?.step ??
          null,
        activeRunStep:
          moduleRunStepsByConfigStepToken[currentState.moduleToken]?.find(
            ({ attributes: { 'config-step-token': token } }) =>
              currentState.selectedToken === token,
          ) ?? null,
      };
    }

    return {
      activeStep: tokensToSteps[currentState.selectedToken]?.step ?? inputStep,
      activeRunStep: tokensToRunSteps[currentState.selectedToken] ?? null,
    };
  }, [
    currentState,
    tokensToSteps,
    tokensToRunSteps,
    inputStep,
    moduleRunStepsByConfigStepToken,
    tokensToModuleBlockSteps,
  ]);

  const selectStep = (
    selectedNodeToken: string | null,
    { moduleToken }: { moduleToken: string | null } = { moduleToken: null },
  ) =>
    updateStepSelectState({ type: 'select', payload: { token: selectedNodeToken, moduleToken } });

  const deselectStep = () => selectStep(null);
  const focusStep = (token: string) => updateStepSelectState({ type: 'focus', payload: token });

  return [
    { ...currentState, activeStep, activeRunStep },
    { selectStep, deselectStep, focusStep },
  ] as const;
};

export default useWorkflowStepSelectionState;
